import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { divIcon } from 'leaflet';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import '../../../main/theme/theme.css';
import './MainLanding.css';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Star as StarIcon,
  MoreVert as MoreVertIcon,
  AddPhotoAlternate as AddPhotoAlternateIcon,
  LocationOn as LocationOnIcon,
  Tag as TagIcon,
  Public as PublicIcon,
  Close as CloseIcon,
  QuestionAnswer as QuestionAnswerIcon,
  EmojiEmotions as EmojiEmotionsIcon,
  Link as LinkIcon,
  OpenInFull as OpenInFullIcon,
  Hotel as HotelIcon,
  Storefront as StorefrontIcon,
} from '@mui/icons-material';

import axios from 'axios';
import axiosInstance from '../../../main/initializers/axios';
import IHotel from '../../../main/interfaces/IHotel';

import {
  AddPostDraftDto,
  CreatePostCommentResponse,
  CreatePostResponse,
  extractCommentId,
  FeedCategoryDto,
  FeedDailyStatsDto,
  FeedTrendingTagDto,
  MAX_IMAGES,
  PostLocationOptionDto,
  PostCommentDto,
  PostDto,
  toAbsoluteUrl,
} from './PostDto';

import { useNotifications } from './NotificationContext';
import NotificationIcon from './NotificationIcon';
import { resolvePageRedirectRoute } from '../../../main/constants/pageRedirect';
import useGetUser from '../../../main/hooks/useGetUser';
import FeedPage from './FeedPage';
import { compressImagesIfNeeded } from '../../../main/utils/imageCompression';
import { ApiConstants } from '../../../main/constants/ApiConstants';
import { buildEntityFileUrl } from '../../../main/utils/fileUrl';
import { useAppTheme } from '../../../main/theme/AppThemeContext';
import AppPreferenceControls from '../../../main/components/appPreferences/AppPreferenceControls';
enum ReportTargetType {
  Post = 1,
  Comment = 2,
}

const FEED_ENDPOINTS = {
  posts: '/api/feed/posts',
  categories: '/api/feed/categories',
  trendingTags: '/api/feed/trending-tags',
  showcase: '/api/feed/showcase',
  locations: '/api/feed/locations',
  dailyStats: '/api/feed/daily-stats',
  comments: '/api/feed/comments',
  like: '/api/feed/like',
  report: '/api/feed/report',
};

const publicApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

type LandingHotel = IHotel & {
  note?: string;
  redirectUrl?: string;
  latitude?: number | null;
  longitude?: number | null;
};

type LandingCompany = {
  id: number;
  name: string;
  code?: string;
  location?: string;
  description?: string;
  website?: string;
  redirectUrl?: string;
  latitude?: number | null;
  longitude?: number | null;
};

type BusinessShowcaseItem = {
  id: string;
  title: string;
  subtitle: string;
  meta: string;
  type: 'hotel' | 'company';
  redirectUrl?: string;
  description: string;
  imageUrl: string;
  actionLabel: string;
};

type FeedShowcaseItem = {
  id: number;
  type: 'hotel' | 'company';
  name?: string;
  code?: string;
  address?: string;
  location?: string;
  note?: string;
  description?: string;
  website?: string;
  redirectUrl?: string;
  latitude?: number | null;
  longitude?: number | null;
};

type FeedLocationItem = {
  id: number;
  name: string;
  type?: string;
  lat?: number | null;
  lng?: number | null;
};

type LandingMapLayer = 'satellite' | 'terrain' | 'streets';

type LandingMapPoint = {
  id: string;
  label: string;
  meta: string;
  kind: 'marker' | 'hotel' | 'company';
  latitude: number;
  longitude: number;
};

type LandingMapSidebarItem = {
  id: string;
  badge: string;
  title: string;
  subtitle: string;
  caption: string;
};

const DURRES_CENTER: [number, number] = [41.3231, 19.4414];

const toFiniteNumberOrNull = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const isValidLatLng = (latitude: unknown, longitude: unknown): latitude is number =>
  typeof latitude === 'number' &&
  typeof longitude === 'number' &&
  Number.isFinite(latitude) &&
  Number.isFinite(longitude) &&
  Math.abs(latitude) <= 90 &&
  Math.abs(longitude) <= 180;

const LANDING_MAP_LAYERS: Record<
  LandingMapLayer,
  {
    label: string;
    url: string;
    attribution: string;
    maxNativeZoom: number;
  }
> = {
  satellite: {
    label: 'Satellite',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    maxNativeZoom: 19,
    attribution: 'Tiles © Esri',
  },
  terrain: {
    label: 'Terrain',
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    maxNativeZoom: 17,
    attribution: 'Map data © OpenStreetMap contributors, SRTM | Map style © OpenTopoMap',
  },
  streets: {
    label: 'Streets',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    maxNativeZoom: 19,
    attribution: '© OpenStreetMap contributors',
  },
};

const normalizeHotel = (hotel: any): LandingHotel => ({
  ...hotel,
  id: Number(hotel?.id ?? hotel?.Id ?? 0),
  companyId: hotel?.companyId ?? hotel?.CompanyId,
  name: hotel?.name ?? hotel?.Name ?? '',
  code: hotel?.code ?? hotel?.Code ?? '',
  address: hotel?.address ?? hotel?.Address ?? '',
  note: hotel?.note ?? hotel?.Note ?? '',
  redirectUrl: hotel?.redirectUrl ?? hotel?.RedirectUrl ?? '',
  timezone: hotel?.timezone ?? hotel?.Timezone ?? '',
  checkInTime: hotel?.checkInTime ?? hotel?.CheckInTime ?? '',
  checkOutTime: hotel?.checkOutTime ?? hotel?.CheckOutTime ?? '',
  currencyId: hotel?.currencyId ?? hotel?.CurrencyId,
  created: hotel?.created ?? hotel?.Created,
  deleted: hotel?.deleted ?? hotel?.Deleted ?? null,
  coverImageUrl: hotel?.coverImageUrl ?? hotel?.CoverImageUrl ?? '',
  latitude: toFiniteNumberOrNull(hotel?.latitude ?? hotel?.Latitude),
  longitude: toFiniteNumberOrNull(hotel?.longitude ?? hotel?.Longitude),
  isPublic: hotel?.isPublic ?? hotel?.IsPublic ?? false,
});

const normalizeCompany = (company: any): LandingCompany => ({
  id: Number(company?.id ?? company?.Id ?? 0),
  name: company?.name ?? company?.Name ?? '',
  code: company?.code ?? company?.Code ?? '',
  location: company?.location ?? company?.Location ?? company?.address ?? company?.Address ?? '',
  description: company?.description ?? company?.Description ?? '',
  website: company?.website ?? company?.Website ?? '',
  redirectUrl: company?.redirectUrl ?? company?.RedirectUrl ?? '',
  latitude: toFiniteNumberOrNull(company?.latitude ?? company?.Latitude),
  longitude: toFiniteNumberOrNull(company?.longitude ?? company?.Longitude),
});

const normalizeFeedShowcaseItem = (item: any): FeedShowcaseItem => ({
  id: Number(item?.id ?? item?.Id ?? 0),
  type: (item?.type ?? item?.Type ?? 'company') === 'hotel' ? 'hotel' : 'company',
  name: item?.name ?? item?.Name ?? '',
  code: item?.code ?? item?.Code ?? '',
  address: item?.address ?? item?.Address ?? '',
  location: item?.location ?? item?.Location ?? '',
  note: item?.note ?? item?.Note ?? '',
  description: item?.description ?? item?.Description ?? '',
  website: item?.website ?? item?.Website ?? '',
  redirectUrl: item?.redirectUrl ?? item?.RedirectUrl ?? '',
  latitude: toFiniteNumberOrNull(item?.latitude ?? item?.Latitude),
  longitude: toFiniteNumberOrNull(item?.longitude ?? item?.Longitude),
});

const toSlug = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-');

const cleanLandingText = (value: unknown, fallback = '') => {
  if (value === null || value === undefined) return fallback;
  const text = String(value).trim();
  return !text || text.toLowerCase() === 'null' || text.toLowerCase() === 'undefined' ? fallback : text;
};

type FloatingFx = {
  id: number;
  left: number;
  bottom: number;
  size: number;
  drift: number;
  duration: number;
  delay: number;
  glyph: string;
};

const createMapPinIcon = (isActive: boolean, kind: LandingMapPoint['kind']) =>
  divIcon({
    className: 'landing-map-pin-shell',
    html: `<span class="landing-map-pin-visual landing-map-pin-visual--${kind}${isActive ? ' is-active' : ''}"></span>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    popupAnchor: [0, -10],
  });

const LandingMapViewportSync: React.FC<{ selectedPoint: LandingMapPoint | null }> = ({ selectedPoint }) => {
  const map = useMap();

  useEffect(() => {
    if (!selectedPoint) return;
    if (!isValidLatLng(selectedPoint.latitude, selectedPoint.longitude)) return;
    if (!(map as any)._loaded) return;

    const nextCenter: [number, number] = [selectedPoint.latitude, selectedPoint.longitude];
    const currentCenter = map.getCenter();
    const currentZoom = map.getZoom();
    const hasValidCurrentView =
      Number.isFinite(currentCenter.lat) &&
      Number.isFinite(currentCenter.lng) &&
      Number.isFinite(currentZoom);

    if (
      hasValidCurrentView &&
      Math.abs(currentCenter.lat - selectedPoint.latitude) < 0.000001 &&
      Math.abs(currentCenter.lng - selectedPoint.longitude) < 0.000001 &&
      currentZoom === 13
    ) {
      return;
    }

    try {
      const size = map.getSize();
      const canAnimate = Number.isFinite(size.x) && Number.isFinite(size.y) && size.x > 0 && size.y > 0;

      if (canAnimate) {
        map.flyTo(nextCenter, 13, {
          animate: true,
          duration: 0.8,
        });
      } else {
        map.setView(nextCenter, 13, { animate: false });
      }
    } catch {
      map.setView(nextCenter, 13, { animate: false });
    }
  }, [map, selectedPoint]);

  return null;
};

const LandingMapLayoutSync: React.FC<{ activeMapLayer: LandingMapLayer; panelMode: 'sidebar' | 'expanded' }> = ({ activeMapLayer, panelMode }) => {
  const map = useMap();

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      map.invalidateSize();
    }, 60);

    return () => window.clearTimeout(timeoutId);
  }, [activeMapLayer, map, panelMode]);

  return null;
};

const MainLandingContent: React.FC = () => {
  const { i18n } = useTranslation();
  const { theme } = useAppTheme();
  const navigate = useNavigate();
  const [posts, setPosts] = useState<PostDto[]>([]);
  const [categories, setCategories] = useState<FeedCategoryDto[]>([]);
  const [trendingHashtags, setTrendingHashtags] = useState<FeedTrendingTagDto[]>([]);
  const [dailyStats, setDailyStats] = useState<FeedDailyStatsDto>({ postsToday: 0, visitorsToday: 0 });
  const [featuredHotels, setFeaturedHotels] = useState<LandingHotel[]>([]);
  const [landingCompanies, setLandingCompanies] = useState<LandingCompany[]>([]);
  const [feedLocations, setFeedLocations] = useState<PostLocationOptionDto[]>([]);
  const [postsPage, setPostsPage] = useState(1);
  const [hasMorePosts, setHasMorePosts] = useState(true);
  const [isFetchingPosts, setIsFetchingPosts] = useState(false);
  const isFetchingPostsRef = useRef(false);
  const hasRequestedInitialPostsRef = useRef(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [showMobileNavMenu, setShowMobileNavMenu] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedMapLocationId, setSelectedMapLocationId] = useState<string | null>(null);
  const [activeMapLayer, setActiveMapLayer] = useState<LandingMapLayer>('satellite');
  const [isExpandedMapOpen, setIsExpandedMapOpen] = useState(false);
  const [selectedBusinessId, setSelectedBusinessId] = useState<string | null>(null);
  const { addNotification } = useNotifications();
  const user = useGetUser();
  const userAvatarUrl = buildEntityFileUrl(user?.id, ApiConstants.EntityTypes.User);
  const userInitial = (user?.userName?.trim()?.[0] || 'U').toUpperCase();
  const [likedPostIds, setLikedPostIds] = useState<Set<number>>(() => new Set());
  const [shareOpenFor, setShareOpenFor] = useState<number | null>(null);
  const [floatingFx, setFloatingFx] = useState<FloatingFx[]>([]);
  const businessSliderViewportRef = useRef<HTMLDivElement | null>(null);
  const businessSliderDragRef = useRef<{ isDragging: boolean; hasDragged: boolean; startX: number; startScrollLeft: number; activeItemId: string | null }>({
    isDragging: false,
    hasDragged: false,
    startX: 0,
    startScrollLeft: 0,
    activeItemId: null,
  });
  const shouldSkipSidebarCalls = useMemo(() => window.matchMedia('(max-width: 992px)').matches, []);
  const failedPostActionsRef = useRef<Set<string>>(new Set());
  const inFlightPostActionsRef = useRef<Set<string>>(new Set());
  const isMountedRef = useRef(true);

  // report modal state
  const [reportOpen, setReportOpen] = useState(false);
  const [reportTargetId, setReportTargetId] = useState<number | null>(null);
  const [reportReason, setReportReason] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  // Keyboard shortcuts
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowSearchResults(false);
        setShowQuickActions(false);
        setShowMobileNavMenu(false);
        setShareOpenFor(null);
        setReportOpen(false);
        setIsExpandedMapOpen(false);
      }
      if (e.key === '/' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        const searchInput = document.querySelector('.search-input') as HTMLElement;
        searchInput?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    performSearch(debouncedSearch);
  }, [debouncedSearch]);

  const closeLandingSearch = useCallback(() => {
    setSearchQuery('');
    setShowSearchResults(false);
  }, []);

  const runLandingPostAction = useCallback(async (actionKey: string, request: () => Promise<any>) => {
    if (failedPostActionsRef.current.has(actionKey)) {
      return { blocked: true, data: null as any };
    }
    if (inFlightPostActionsRef.current.has(actionKey)) {
      return { blocked: true, data: null as any };
    }

    inFlightPostActionsRef.current.add(actionKey);
    try {
      const data = await request();
      return { blocked: false, data };
    } catch (error) {
      failedPostActionsRef.current.add(actionKey);
      throw error;
    } finally {
      inFlightPostActionsRef.current.delete(actionKey);
    }
  }, []);

  const isLandingPostActionBlocked = useCallback((actionKey: string) => {
    return failedPostActionsRef.current.has(actionKey) || inFlightPostActionsRef.current.has(actionKey);
  }, []);

  const suggestedTags = useMemo(() => trendingHashtags.map((tag) => tag.tag.replace(/^#/, '')).filter(Boolean), [trendingHashtags]);

  const locationOptions = useMemo<PostLocationOptionDto[]>(() => {
    const hotelOptions = featuredHotels.map((hotel) => ({
      id: `hotel-${hotel.id}`,
      label: hotel.name || 'Hotel',
      meta: hotel.address || i18n.t('mainLanding.search.defaultHotelAddress', { defaultValue: 'Durres, Albania' }),
      latitude: hotel.latitude ?? null,
      longitude: hotel.longitude ?? null,
      type: 'Hotel',
      mapTargetId: `hotel-${hotel.id}`,
    }));

    const companyOptions = landingCompanies.map((company) => ({
      id: `company-${company.id}`,
      label: company.name || 'Business',
      meta: company.location || company.website || i18n.t('mainLanding.search.businessFallback', { defaultValue: 'Business partner' }),
      latitude: company.latitude ?? null,
      longitude: company.longitude ?? null,
      type: 'Business',
      mapTargetId: `company-${company.id}`,
    }));

    const seen = new Set<string>();
    return [...feedLocations, ...hotelOptions, ...companyOptions].filter((item) => {
      const key = `${item.label.trim().toLowerCase()}::${(item.type ?? '').trim().toLowerCase()}`;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [feedLocations, featuredHotels, i18n, landingCompanies]);

  const searchResults = useMemo(() => {
    const query = debouncedSearch.trim().toLowerCase();
    if (!query) return { posts: [] as PostDto[], hotels: [] as LandingHotel[], companies: [] as LandingCompany[] };

    return {
      posts: posts.filter((post) => `${post.content} ${post.userName ?? ''} ${post.locationName ?? ''}`.toLowerCase().includes(query)).slice(0, 6),
      hotels: featuredHotels
        .filter((hotel) => `${hotel.name} ${hotel.address ?? ''} ${hotel.note ?? ''}`.toLowerCase().includes(query))
        .slice(0, 6),
      companies: landingCompanies
        .filter((company) => `${company.name} ${company.location ?? ''} ${company.description ?? ''} ${company.website ?? ''}`.toLowerCase().includes(query))
        .slice(0, 6),
    };
  }, [debouncedSearch, featuredHotels, landingCompanies, posts]);

  const hasSearchResults = searchResults.posts.length > 0 || searchResults.hotels.length > 0 || searchResults.companies.length > 0;
  const mapPoints = useMemo<LandingMapPoint[]>(() => {
    const locationPoints = feedLocations
      .filter((location) => isValidLatLng(location.latitude, location.longitude))
      .map((location) => ({
        id: location.mapTargetId || location.id,
        label: location.label,
        meta: location.meta || location.type || i18n.t('mainLanding.map.locationFallback', { defaultValue: 'Saved location' }),
        kind: 'marker' as const,
        latitude: location.latitude as number,
        longitude: location.longitude as number,
      }));

    const hotelPoints = featuredHotels
      .filter((hotel) => isValidLatLng(hotel.latitude, hotel.longitude))
      .map((hotel) => {
        return {
        id: `hotel-${hotel.id}`,
        label: hotel.name || 'Hotel',
        meta: hotel.address || i18n.t('mainLanding.search.defaultHotelAddress', { defaultValue: 'Durres, Albania' }),
        kind: 'hotel' as const,
        latitude: hotel.latitude as number,
        longitude: hotel.longitude as number,
      };
      });

    const companyPoints = landingCompanies
      .filter((company) => isValidLatLng(company.latitude, company.longitude))
      .map((company) => {
        return {
        id: `company-${company.id}`,
        label: company.name || 'Business',
        meta: company.location || company.website || i18n.t('mainLanding.search.businessFallback', { defaultValue: 'Business partner' }),
        kind: 'company' as const,
        latitude: company.latitude as number,
        longitude: company.longitude as number,
      };
      });

    const seen = new Set<string>();
    return [...locationPoints, ...hotelPoints, ...companyPoints].filter((point) => {
      if (seen.has(point.id)) return false;
      seen.add(point.id);
      return true;
    });
  }, [feedLocations, featuredHotels, i18n, landingCompanies]);

  const selectedMapPoint = useMemo(
    () => mapPoints.find((point) => point.id === selectedMapLocationId) ?? null,
    [mapPoints, selectedMapLocationId],
  );
  const activeLayerConfig = LANDING_MAP_LAYERS[activeMapLayer];

  useEffect(() => {
    if (!mapPoints.length) {
      setSelectedMapLocationId(null);
      return;
    }

    setSelectedMapLocationId((prev) => (prev && mapPoints.some((point) => point.id === prev) ? prev : mapPoints[0].id));
  }, [mapPoints]);

  const mapSidebarItems = useMemo<LandingMapSidebarItem[]>(() => {
    return mapPoints.map((point, index) => ({
      id: point.id,
      badge: point.kind === 'hotel' ? 'H' : point.kind === 'company' ? 'B' : `${index + 1}`,
      title: point.label,
      subtitle:
        point.kind === 'hotel'
          ? i18n.t('mainLanding.businessCard.hotel', { defaultValue: 'Hotel' })
          : point.kind === 'company'
            ? i18n.t('mainLanding.businessCard.company', { defaultValue: 'Business' })
            : i18n.t('mainLanding.map.locationBadge', { defaultValue: 'Location' }),
      caption: point.meta,
    }));
  }, [i18n, mapPoints]);

  const performSearch = (query: string) => {
    setShowSearchResults(Boolean(query.trim()));
  };

  const handleBusinessSliderPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const viewport = businessSliderViewportRef.current;
    if (!viewport) return;
    if (e.pointerType === 'touch') return;
    businessSliderDragRef.current = {
      isDragging: true,
      hasDragged: false,
      startX: e.clientX,
      startScrollLeft: viewport.scrollLeft,
      activeItemId: ((e.target as HTMLElement | null)?.closest('[data-business-card-id]') as HTMLElement | null)?.dataset.businessCardId ?? null,
    };
    viewport.setPointerCapture?.(e.pointerId);
  };

  const handleBusinessSliderPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const viewport = businessSliderViewportRef.current;
    const drag = businessSliderDragRef.current;
    if (!viewport || !drag.isDragging) return;
    const deltaX = e.clientX - drag.startX;
    if (Math.abs(deltaX) > 6) {
      drag.hasDragged = true;
    }
    viewport.scrollLeft = drag.startScrollLeft - deltaX;
  };

  const stopBusinessSliderDrag = (pointerId?: number) => {
    const viewport = businessSliderViewportRef.current;
    businessSliderDragRef.current.isDragging = false;
    if (viewport && typeof pointerId === 'number' && viewport.hasPointerCapture?.(pointerId)) {
      viewport.releasePointerCapture(pointerId);
    }
  };

  const handleBusinessSliderPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = businessSliderDragRef.current;
    if (!drag.hasDragged && drag.activeItemId) {
      setSelectedBusinessId(drag.activeItemId);
    }
    stopBusinessSliderDrag(e.pointerId);
  };

  const moveBusinessSlider = (direction: 'left' | 'right') => {
    const viewport = businessSliderViewportRef.current;
    if (!viewport) return;
    const cardWidth = viewport.querySelector<HTMLElement>('.landing-business-card')?.offsetWidth ?? 240;
    viewport.scrollBy({
      left: direction === 'right' ? cardWidth + 14 : -(cardWidth + 14),
      behavior: 'smooth',
    });
  };

  const spawnFloatingFx = useCallback((intensity: 'sm' | 'md' | 'lg' = 'md') => {
    const total = intensity === 'sm' ? 6 : intensity === 'lg' ? 14 : 10;
    const now = Date.now();
    const glyphs = ['❤', '✨', '💫'];

    const next: FloatingFx[] = Array.from({ length: total }, (_, i) => ({
      id: now + i,
      left: 38 + Math.random() * 28,
      bottom: 8 + Math.random() * 10,
      size: 12 + Math.random() * 14,
      drift: -40 + Math.random() * 80,
      duration: 1200 + Math.random() * 900,
      delay: Math.random() * 200,
      glyph: glyphs[Math.floor(Math.random() * glyphs.length)],
    }));

    setFloatingFx((prev) => [...prev, ...next]);

    window.setTimeout(() => {
      setFloatingFx((prev) => prev.filter((item) => !next.some((n) => n.id === item.id)));
    }, 2400);
  }, []);

  const renderMapSidebarSection = (panelMode: 'sidebar' | 'expanded') => (
    <div className={`sidebar-section map-sidebar-section ${panelMode === 'expanded' ? 'map-sidebar-section--expanded' : ''}`}>
      <div className="map-card-header">
        <div className="map-card-header__topRow">
          {panelMode === 'expanded'&&
                 <div>
            <h3 className="sidebar-title">
              <LocationOnIcon />
              {i18n.t('mainLanding.map.title', { defaultValue: 'Explore on map' })}
            </h3>
          </div>
          }
        </div>

        <div className="landing-map-layer-switcher" role="tablist" aria-label={i18n.t('mainLanding.map.styleLabel', { defaultValue: 'Map style' })}>
          {(Object.entries(LANDING_MAP_LAYERS) as Array<[LandingMapLayer, (typeof LANDING_MAP_LAYERS)[LandingMapLayer]]>).map(([layerKey, layer]) => (
            <button
              key={`${panelMode}-${layerKey}`}
              type="button"
              className={`landing-map-layer-btn ${activeMapLayer === layerKey ? 'is-active' : ''}`}
              onClick={() => setActiveMapLayer(layerKey)}
            >
              {i18n.t(`mainLanding.map.layers.${layerKey}`, { defaultValue: layer.label })}
            </button>
          ))}
           <button
            type="button"
            className="landing-map-expand-btn"
            onClick={() => setIsExpandedMapOpen((prev) => !prev)}
            aria-label={
              panelMode === 'expanded'
                ? i18n.t('mainLanding.map.closeExpanded', { defaultValue: 'Close expanded map' })
                : i18n.t('mainLanding.map.expand', { defaultValue: '' })
            }
          >
            {panelMode === 'expanded' ? <CloseIcon /> : <OpenInFullIcon />}
            <span>
              {panelMode === 'expanded'
                ? i18n.t('mainLanding.map.closeExpandedLabel', { defaultValue: 'Close' })
                : i18n.t('mainLanding.map.expandLabel', { defaultValue: '' })}
            </span>
          </button>
        </div>
      </div>

      <div className="landing-map-body">
        <div className="durres-map-card">
          <MapContainer
            center={selectedMapPoint && isValidLatLng(selectedMapPoint.latitude, selectedMapPoint.longitude) ? [selectedMapPoint.latitude, selectedMapPoint.longitude] : DURRES_CENTER}
            zoom={selectedMapPoint ? 13 : 12}
            minZoom={5}
            maxZoom={activeLayerConfig.maxNativeZoom}
            scrollWheelZoom={panelMode === 'expanded'}
            className={`landing-leaflet-map ${panelMode === 'expanded' ? 'landing-leaflet-map--expanded' : ''}`}
          >
            <TileLayer
              key={`${panelMode}-${activeMapLayer}`}
              attribution={activeLayerConfig.attribution}
              url={activeLayerConfig.url}
              maxZoom={activeLayerConfig.maxNativeZoom}
              maxNativeZoom={activeLayerConfig.maxNativeZoom}
            />
            <LandingMapViewportSync selectedPoint={selectedMapPoint} />
            <LandingMapLayoutSync activeMapLayer={activeMapLayer} panelMode={panelMode} />
            {mapPoints.map((point) => (
              <Marker
                key={`${panelMode}-${point.id}`}
                position={[point.latitude, point.longitude]}
                icon={createMapPinIcon(selectedMapLocationId === point.id, point.kind)}
                eventHandlers={{
                  click: () => setSelectedMapLocationId(point.id),
                }}
              >
                <Popup>
                  <strong>{point.label}</strong>
                  <div>{point.meta}</div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
          <div className="landing-map-footer">
            <span>
              {i18n.t('mainLanding.map.currentView', {
                defaultValue: '{{layer}} view',
                layer: i18n.t(`mainLanding.map.layers.${activeMapLayer}`, { defaultValue: activeLayerConfig.label }),
              })}
            </span>
            <strong>{i18n.t('mainLanding.map.placesCount', { defaultValue: '{{count}} places', count: mapPoints.length })}</strong>
          </div>
        </div>

        <div className="map-marker-list">
          {mapSidebarItems.map((item) => (
            <button
              key={`${panelMode}-${item.id}`}
              type="button"
              className={`map-marker-item ${selectedMapLocationId === item.id ? 'is-active' : ''}`}
              onClick={() => setSelectedMapLocationId(item.id)}
            >
              <div className="map-marker-badge">{item.badge}</div>
              <div className="map-marker-copy">
                <strong>{item.title}</strong>
                <span>{item.subtitle}</span>
                <small>{item.caption}</small>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
  const fetchPostsPage = useCallback(
    async (pageToLoad: number, append: boolean) => {
      if (isFetchingPostsRef.current) return;
      isFetchingPostsRef.current = true;
      if (isMountedRef.current) {
        setIsFetchingPosts(true);
      }
      try {
        const response = await publicApi.get<PostDto[]>(FEED_ENDPOINTS.posts, {
          params: { page: pageToLoad, pageSize: 5 },
        });
        const data = response.data ?? [];

        if (!isMountedRef.current) return;

        setLikedPostIds((prev) => {
          const likes = append ? new Set(prev) : new Set<number>();
          for (const item of data) {
            if (item.isLikedByUser) likes.add(item.id);
          }
          return likes;
        });

        setPosts((prev) => {
          if (!append) return data;
          const existing = new Set(prev.map((p) => p.id));
          const incoming = data.filter((p) => !existing.has(p.id));
          return [...prev, ...incoming];
        });

        setHasMorePosts(data.length === 5);
        setPostsPage(pageToLoad);
      } catch (err) {
        console.error('Error fetching posts:', err);
        if (isMountedRef.current) {
          addNotification(i18n.t('mainLanding.feedback.loadPostsError', { defaultValue: 'Failed to load posts' }));
        }
      } finally {
        isFetchingPostsRef.current = false;
        if (isMountedRef.current) {
          setIsFetchingPosts(false);
        }
      }
    },
    [addNotification, i18n],
  );

  useEffect(() => {
    if (hasRequestedInitialPostsRef.current) return;
    hasRequestedInitialPostsRef.current = true;
    void fetchPostsPage(1, false);
  }, [fetchPostsPage]);

  useEffect(() => {
    if (shouldSkipSidebarCalls) return;
    let cancelled = false;

    void publicApi
      .get<FeedCategoryDto[]>(FEED_ENDPOINTS.categories)
      .then((res) => {
        if (!cancelled) setCategories(res.data ?? []);
      })
      .catch(() => {});

    void publicApi
      .get<FeedTrendingTagDto[]>(FEED_ENDPOINTS.trendingTags)
      .then((res) => {
        if (!cancelled) setTrendingHashtags(res.data ?? []);
      })
      .catch(() => {});

    void publicApi
      .get<FeedDailyStatsDto>(FEED_ENDPOINTS.dailyStats)
      .then((res) => {
        if (!cancelled) setDailyStats(res.data ?? { postsToday: 0, visitorsToday: 0 });
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [shouldSkipSidebarCalls]);

  useEffect(() => {
    let cancelled = false;
    void publicApi
      .get<FeedLocationItem[]>(FEED_ENDPOINTS.locations, { params: { take: 40 } })
      .then((res) => {
        if (cancelled) return;
        const items = Array.isArray(res.data) ? res.data : [];
        setFeedLocations(
          items.map((location) => ({
            id: `location-${location.id}`,
            label: location.name,
            meta: location.type || '',
            locationId: location.id,
            latitude: toFiniteNumberOrNull(location.lat),
            longitude: toFiniteNumberOrNull(location.lng),
            type: location.type || 'Place'
          })),
        );
      })
      .catch(() => {
        if (!cancelled) setFeedLocations([]);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void publicApi
      .get<FeedShowcaseItem[]>(FEED_ENDPOINTS.showcase)
      .then((res) => {
        if (cancelled) return;
        const items = Array.isArray(res.data) ? res.data.map(normalizeFeedShowcaseItem) : [];

        const hotels = items
          .filter((item) => item.type === 'hotel')
          .map((hotel) =>
            normalizeHotel({
              id: hotel.id,
              name: hotel.name,
              code: hotel.code,
              address: hotel.address,
              note: hotel.note,
              redirectUrl: hotel.redirectUrl,
              latitude: hotel.latitude,
              longitude: hotel.longitude,
            }),
          );

        const companies = items
          .filter((item) => item.type === 'company')
          .map((company) =>
            normalizeCompany({
              id: company.id,
              name: company.name,
              code: company.code,
              location: company.location,
              description: company.description,
              website: company.website,
              redirectUrl: company.redirectUrl,
              latitude: company.latitude,
              longitude: company.longitude,
            }),
          );

        setFeaturedHotels(hotels.filter((hotel) => !hotel.deleted));
        setLandingCompanies(companies);
      })
      .catch(() => {
        if (!cancelled) {
          setFeaturedHotels([]);
          setLandingCompanies([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const businessShowcase = useMemo<BusinessShowcaseItem[]>(() => {
    const hotels = featuredHotels.map((hotel) => ({
      id: `hotel-${hotel.id}`,
      title: cleanLandingText(hotel.name, 'Hotel'),
      subtitle: cleanLandingText(hotel.address, i18n.t('mainLanding.search.defaultHotelAddress', { defaultValue: 'Durres, Albania' })),
      meta: i18n.t('mainLanding.businessCard.hotel', { defaultValue: 'Hotel' }),
      type: 'hotel' as const,
      redirectUrl: hotel.redirectUrl,
      description: cleanLandingText(
        hotel.note,
        i18n.t('mainLanding.showcase.hotelDescription', {
          defaultValue: 'Discover rooms, availability, and booking options for this hotel.',
        }),
      ),
      imageUrl: buildEntityFileUrl(hotel.id, ApiConstants.EntityTypes.Hotel),
      actionLabel: i18n.t('mainLanding.showcase.viewHotel', { defaultValue: 'View hotel' }),
    }));

    const companies = landingCompanies.map((company) => ({
      id: `company-${company.id}`,
      title: cleanLandingText(company.name, 'Company'),
      subtitle:
        cleanLandingText(company.location) ||
        cleanLandingText(company.website) ||
        i18n.t('mainLanding.search.businessFallback', { defaultValue: 'Business partner' }),
      meta: i18n.t('mainLanding.businessCard.company', { defaultValue: 'Business' }),
      type: 'company' as const,
      redirectUrl: company.redirectUrl,
      description: cleanLandingText(
        company.description,
        i18n.t('mainLanding.showcase.companyDescription', {
          defaultValue: 'Explore the menu, services, and public page for this business.',
        }),
      ),
      imageUrl: buildEntityFileUrl(company.id, ApiConstants.EntityTypes.Company),
      actionLabel: i18n.t('mainLanding.showcase.viewMenu', { defaultValue: 'View more' }),
    }));

    return [...companies, ...hotels];
  }, [featuredHotels, i18n, landingCompanies]);

  const selectedBusinessShowcase = useMemo(
    () => businessShowcase.find((item) => item.id === selectedBusinessId) ?? businessShowcase[0] ?? null,
    [businessShowcase, selectedBusinessId],
  );

  useEffect(() => {
    if (!businessShowcase.length) {
      setSelectedBusinessId(null);
      return;
    }

    if (!selectedBusinessId || !businessShowcase.some((item) => item.id === selectedBusinessId)) {
      setSelectedBusinessId(businessShowcase[0].id);
    }
  }, [businessShowcase, selectedBusinessId]);

  const sidebarHotels = useMemo(() => featuredHotels.slice(0, 6), [featuredHotels]);

  const openBusinessShowcaseUrl = useCallback(
    (redirectUrl?: string) => {
      const nextUrl = (redirectUrl ?? '').trim();
      if (!nextUrl) return;

      if (/^https?:\/\//i.test(nextUrl)) {
        window.open(nextUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      navigate(nextUrl);
    },
    [navigate],
  );

  useEffect(() => {
    const onScroll = () => {
      if (isFetchingPosts || !hasMorePosts) return;
      const scrollPos = window.innerHeight + window.scrollY;
      const threshold = document.body.offsetHeight - 300;
      if (scrollPos >= threshold) {
        void fetchPostsPage(postsPage + 1, true);
      }
    };

    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [fetchPostsPage, hasMorePosts, isFetchingPosts, postsPage]);

  // --- helpers for safe state updates ---
  const updatePost = useCallback((postId: number, updater: (p: PostDto) => PostDto) => {
    setPosts((prev) => prev.map((p) => (p.id === postId ? updater(p) : p)));
  }, []);

  const getPostShareUrl = (postId: number) => {
    // if you have a real route, use it:
    // return `${window.location.origin}/post/${postId}`;
    return `${window.location.origin}${window.location.pathname}?post=${postId}`;
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      addNotification(i18n.t('mainLanding.feedback.linkCopied', { defaultValue: 'Link copied' }));
    } catch {
      // fallback
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      addNotification(i18n.t('mainLanding.feedback.linkCopied', { defaultValue: 'Link copied' }));
    }
  };

  // =========================
  // COMMENTS (already optimized)
  // =========================
  const handleAddComment = async (postId: number, commentText: string) => {
    if (!user) {
      navigate('/login');
      return;
    }

    const clean = commentText.trim();
    if (!clean) return;
    if (isLandingPostActionBlocked('comments')) return;

    const tempCommentId = -Date.now();
    const optimistic: PostCommentDto = {
      id: tempCommentId,
      userId: user.id ?? 2,
      comment: clean,
      createdAt: new Date().toISOString(),
      userName: user.userName || `User${user.id ?? 2}`,
    };

    updatePost(postId, (p) => ({ ...p, comments: [...(p.comments ?? []), optimistic] }));

    try {
      const guarded = await runLandingPostAction('comments', () =>
        axiosInstance.post<CreatePostCommentResponse>(FEED_ENDPOINTS.comments, {
          postId,
          comment: clean,
        }),
      );
      if (guarded.blocked) return;

      const response = guarded.data;

      const realId = extractCommentId(response.data);

      updatePost(postId, (p) => ({
        ...p,
        comments: (p.comments ?? []).map((c) => (c.id === tempCommentId ? { ...c, id: realId } : c)),
      }));

      addNotification(i18n.t('mainLanding.feedback.commentAdded', { defaultValue: 'Comment added successfully' }));
    } catch (err) {
      console.error('Error adding comment:', err);

      updatePost(postId, (p) => ({
        ...p,
        comments: (p.comments ?? []).filter((c) => c.id !== tempCommentId),
      }));

      if (axios.isAxiosError(err)) {
        const errorMessage =
          (typeof err.response?.data === 'string' ? err.response.data : (err.response?.data as any)?.message) || 'Dështoi në shtimin e komentit';
        addNotification(errorMessage);
      } else {
        addNotification(i18n.t('mainLanding.feedback.commentAddError', { defaultValue: 'Failed to add comment' }));
      }
    }
  };

  // =========================
  // LIKE (optimized: no fetchPosts)
  // =========================
  const toggleLike = useCallback(
    async (postId: number) => {
      if (!user) {
        navigate('/login');
        return;
      }
      const wasLiked = likedPostIds.has(postId);
      const nextLiked = !wasLiked;
      if (isLandingPostActionBlocked('like')) return;
      if (nextLiked) {
        spawnFloatingFx('sm');
      }

      // optimistic UI
      setLikedPostIds((prev) => {
        const copy = new Set(prev);
        if (nextLiked) copy.add(postId);
        else copy.delete(postId);
        return copy;
      });

      updatePost(postId, (p) => ({
        ...p,
        likeCount: Math.max(0, (p.likeCount ?? 0) + (nextLiked ? 1 : -1)),
      }));

      try {
        const guarded = await runLandingPostAction('like', () =>
          axiosInstance.post<any>(FEED_ENDPOINTS.like, {
            targetId: postId,
            targetType: ReportTargetType.Post,
          }),
        );
        if (guarded.blocked) return;

        const res = guarded.data;

        // If backend returns authoritative counts, use them (optional)
        const serverLikeCount = res?.data?.likeCount ?? res?.data?.count;
        const serverLiked = res?.data?.liked;

        if (typeof serverLikeCount === 'number') {
          updatePost(postId, (p) => ({ ...p, likeCount: Math.max(0, serverLikeCount) }));
        }
        if (typeof serverLiked === 'boolean') {
          setLikedPostIds((prev) => {
            const copy = new Set(prev);
            if (serverLiked) copy.add(postId);
            else copy.delete(postId);
            return copy;
          });
        }
      } catch (err) {
        // rollback
        setLikedPostIds((prev) => {
          const copy = new Set(prev);
          if (wasLiked) copy.add(postId);
          else copy.delete(postId);
          return copy;
        });

        updatePost(postId, (p) => ({
          ...p,
          likeCount: Math.max(0, (p.likeCount ?? 0) + (wasLiked ? 1 : -1)),
        }));

        addNotification(i18n.t('mainLanding.feedback.likeError', { defaultValue: 'Failed to like the post' }));
      }
    },
    [addNotification, isLandingPostActionBlocked, likedPostIds, navigate, runLandingPostAction, updatePost, user],
  );

  // =========================
  // REPORT (modal + backend call)
  // =========================
  const openReport = (postId: number) => {
    if (!user) {
      navigate('/login');
      return;
    }
    setReportTargetId(postId);
    setReportReason('');
    setReportOpen(true);
  };

  const submitReport = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (!reportTargetId) return;

    const reason = reportReason.trim();
    if (!reason) {
      addNotification(i18n.t('mainLanding.report.required', { defaultValue: 'Please write the report reason' }));
      return;
    }

    try {
      setReportSubmitting(true);
      const guarded = await runLandingPostAction('report', () =>
        axiosInstance.post(FEED_ENDPOINTS.report, {
          targetId: reportTargetId,
          targetType: ReportTargetType.Post,
          reason,
        }),
      );
      if (guarded.blocked) return;
      addNotification(i18n.t('mainLanding.report.success', { defaultValue: 'Report sent successfully' }));
      setReportOpen(false);
    } catch (err) {
      addNotification(i18n.t('mainLanding.report.error', { defaultValue: 'Failed to send the report' }));
    } finally {
      setReportSubmitting(false);
    }
  };

  // =========================
  // ADD POST (kept as you had it)
  // =========================
  const handleAddPost = async (
    content: string,
    images: File[] = [],
    extras?: Partial<AddPostDraftDto>,
  ) => {
    if (!user) {
      navigate('/login');
      return;
    }

    const cleanContent = content.trim();
    const safeImages = images.slice(0, MAX_IMAGES);

    if (!cleanContent && safeImages.length === 0) {
      addNotification(i18n.t('mainLanding.feedback.postContentRequired', { defaultValue: 'Write something or add at least one photo' }));
      return;
    }

    if (isLandingPostActionBlocked('create-post')) return;

    let compressedImages: File[] = safeImages;
    try {
      compressedImages = await compressImagesIfNeeded(safeImages, { maxKB: 200 });
    } catch {
      compressedImages = safeImages;
    }
    const tempId = -Date.now();
    const optimistic: PostDto = {
      id: tempId,
      userId: user.id ?? 2,
      userName: user.userName,
      content: cleanContent,
      createdAt: new Date().toISOString(),
      locationId: extras?.locationId ?? null,
      locationName: extras?.locationName ?? null,
      categoryId: null,
      categoryName: null,
      likeCount: 0,
      images: compressedImages.map((f) => URL.createObjectURL(f)),
      tags: extras?.tags
        ? extras.tags
            .split(',')
            .map((tag) => tag.trim())
            .filter(Boolean)
        : [],
      comments: [],
    };

    setPosts((prev) => [optimistic, ...prev]);
    spawnFloatingFx('lg');

    try {
      const formData = new FormData();
      formData.append('content', cleanContent);
      if (typeof extras?.locationId === 'number') {
        formData.append('locationId', String(extras.locationId));
      }
      if (extras?.tags?.trim()) {
        formData.append('tags', extras.tags.trim());
      }
      compressedImages.forEach((img) => formData.append('files', img));

      const guarded = await runLandingPostAction('create-post', () =>
        axiosInstance.post<CreatePostResponse>(FEED_ENDPOINTS.posts.replace('/posts', '/posts/create'), formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        }),
      );
      if (guarded.blocked) return;

      addNotification(i18n.t('mainLanding.feedback.postAdded', { defaultValue: 'Post added successfully' }));
      // optionally fetchPosts() if you want server to return exact images paths, etc.
    } catch (err) {
      setPosts((prev) => prev.filter((p) => p.id !== tempId));
      const errorMessage =
        axios.isAxiosError(err) &&
        ((typeof err.response?.data === 'string' && err.response.data) || (err.response?.data as any)?.message)
          ? (typeof err.response?.data === 'string' ? err.response.data : (err.response?.data as any)?.message)
          : 'Deshtoi ne krijimin e postimit';
      addNotification(errorMessage);
    } finally {
      optimistic.images.forEach((u) => URL.revokeObjectURL(u));
    }
  };
  const onDeletePost = async (postId: number) => {
    if (!window.confirm(i18n.t('mainLanding.feedback.deleteConfirm', { defaultValue: 'Are you sure you want to delete this post?' }))) return;

    try {
      await axiosInstance.delete(`${FEED_ENDPOINTS.posts}?id=${postId}`);
      setPosts((prev) => prev.filter((p) => p.id !== postId));
      addNotification(i18n.t('mainLanding.feedback.postDeleted', { defaultValue: 'Post deleted successfully' }));
    } catch (err) {
      console.error('Error deleting post:', err);
      addNotification(i18n.t('mainLanding.feedback.postDeleteError', { defaultValue: 'Failed to delete the post' }));
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/';
  };

  // =========================
  // AddPost Component (same style, unchanged logic)
  // =========================
  const AddPost: React.FC<{ onAddPost: (content: string, images?: File[]) => void }> = ({ onAddPost }) => {
    const [content, setContent] = useState('');
    const [images, setImages] = useState<File[]>([]);
    const [imagePreviews, setImagePreviews] = useState<string[]>([]);
    const [privacy, setPrivacy] = useState('public');
    const [isExpanded, setIsExpanded] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const MAX_IMAGES_LOCAL = 5;
    const MAX_CHARS = 500;

    const canSubmit = content.trim().length > 0 || images.length > 0;
    const isContentTooLong = content.length > MAX_CHARS;

    const handleSubmit = () => {
      if (!canSubmit) return;
      if (isContentTooLong) return;

      onAddPost(content.trim(), images);
      resetForm();
      setIsExpanded(false);
    };

    const resetForm = () => {
      setContent('');
      setImages([]);
      imagePreviews.forEach((url) => URL.revokeObjectURL(url));
      setImagePreviews([]);
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files) return;

      const MAX_FILE_SIZE_MB = 1;
      const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
      const newFiles = Array.from(e.target.files);
      const remainingSlots = MAX_IMAGES_LOCAL - images.length;
      const oversized = newFiles.filter((file) => file.size > MAX_FILE_SIZE_BYTES);

      if (oversized.length > 0) {
        alert(i18n.t('mainLanding.addPost.imageSizeLimit', { defaultValue: 'Each photo must be at most 1MB.' }));
        e.currentTarget.value = '';
        return;
      }

      if (newFiles.length > remainingSlots) {
        alert(i18n.t('mainLanding.addPost.maxImagesLocal', { defaultValue: 'You can upload a maximum of {{count}} photos.', count: MAX_IMAGES_LOCAL }));
        e.currentTarget.value = '';
        return;
      }

      const validFiles = newFiles.slice(0, remainingSlots);
      const newImages = [...images, ...validFiles];
      setImages(newImages);

      const newPreviews = validFiles.map((file) => URL.createObjectURL(file));
      setImagePreviews((prev) => [...prev, ...newPreviews]);

      if (fileInputRef.current) fileInputRef.current.value = '';
      e.currentTarget.value = '';
    };
    const removeImage = (index: number) => {
      const newImages = images.filter((_, i) => i !== index);
      setImages(newImages);

      URL.revokeObjectURL(imagePreviews[index]);
      const newPreviews = imagePreviews.filter((_, i) => i !== index);
      setImagePreviews(newPreviews);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSubmit();
    };

    const openFileSelector = () => fileInputRef.current?.click();

    const getCharCountColor = () => {
      const percentage = (content.length / MAX_CHARS) * 100;
      if (percentage > 90) return '#ef4444';
      if (percentage > 75) return '#f59e0b';
      return '#6b7280';
    };

    return (
      <div className={`add-post-modern ${isExpanded ? 'expanded' : ''}`}>
        <div className="post-header">
          <div className="post-input-header" onClick={() => setIsExpanded(true)}>
            <textarea
              className="post-textarea-modern"
              placeholder={i18n.t('mainLanding.addPost.placeholderShort', { defaultValue: 'Share it with us...' })}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={isExpanded ? 4 : 1}
              maxLength={MAX_CHARS}
            />
            {!isExpanded && (
              <div className="post-actions-mini">
                <button type="button" className="action-mini" title={i18n.t('mainLanding.addPost.addPhoto', { defaultValue: 'Add photo' })} onClick={openFileSelector}>
                  <AddPhotoAlternateIcon />
                </button>
                <button type="button" className="action-mini" title={i18n.t('mainLanding.addPost.addEmoji', { defaultValue: 'Add emoji' })}>
                  <EmojiEmotionsIcon />
                </button>
                <button type="button" className="action-mini" title={i18n.t('mainLanding.addPost.addLocation', { defaultValue: 'Add location' })}>
                  <LocationOnIcon />
                </button>
              </div>
            )}
          </div>
        </div>

        {isExpanded && (
          <>
            {imagePreviews.length > 0 && (
              <div className="image-grid-modern">
                {imagePreviews.map((preview, index) => (
                  <div key={index} className="image-preview-modern">
                    <img src={toAbsoluteUrl(preview)} alt={`Preview ${index + 1}`} loading="lazy" />
                    <button type="button" className="remove-image-modern" onClick={() => removeImage(index)}>
                      <CloseIcon fontSize="small" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="post-options">
              <div className="privacy-selector">
                <PublicIcon fontSize="small" />
                <select value={privacy} onChange={(e) => setPrivacy(e.target.value)} className="privacy-select">
                  <option value="public">{i18n.t('mainLanding.addPost.public', { defaultValue: 'Public' })}</option>
                  <option value="friends">{i18n.t('mainLanding.addPost.friends', { defaultValue: 'Friends' })}</option>
                  <option value="private">{i18n.t('mainLanding.addPost.private', { defaultValue: 'Private' })}</option>
                </select>
              </div>

              <div className="char-count-modern" style={{ color: getCharCountColor() }}>
                {content.length}/{MAX_CHARS}
              </div>
            </div>

            <div className="post-footer-modern">
              <div className="action-buttons-modern">
                <button type="button" className="action-btn-modern" onClick={openFileSelector} disabled={images.length >= MAX_IMAGES_LOCAL}>
                  <AddPhotoAlternateIcon />
                  <span>{i18n.t('mainLanding.addPost.photo', { defaultValue: 'Photo' })}</span>
                  {images.length > 0 && <span className="photo-count-badge">{images.length}</span>}
                </button>

                <button type="button" className="action-btn-modern">
                  <EmojiEmotionsIcon />
                  <span>{i18n.t('mainLanding.addPost.emoji', { defaultValue: 'Emoji' })}</span>
                </button>

                <button type="button" className="action-btn-modern">
                  <LocationOnIcon />
                  <span>{i18n.t('mainLanding.addPost.location', { defaultValue: 'Location' })}</span>
                </button>

                <button type="button" className="action-btn-modern">
                  <TagIcon />
                  <span>{i18n.t('mainLanding.addPost.tag', { defaultValue: 'Tag' })}</span>
                </button>
              </div>

              <div className="submit-buttons">
                <button type="button" className="cancel-btn-modern" onClick={() => setIsExpanded(false)}>
                  {i18n.t('mainLanding.actions.cancel', { defaultValue: 'Cancel' })}
                </button>

                <button
                  type="button"
                  className={`submit-btn-modern ${canSubmit && !isContentTooLong ? 'active' : ''}`}
                  onClick={handleSubmit}
                  disabled={!canSubmit || isContentTooLong}
                >
                  {i18n.t('mainLanding.addPost.submit', { defaultValue: 'Post' })}
                </button>
              </div>
            </div>
          </>
        )}

        <input type="file" ref={fileInputRef} onChange={handleImageChange} accept="image/*" multiple style={{ display: 'none' }} />
      </div>
    );
  };

  return (
    <div className={`app-container bcShell bcTheme-${theme}`}>
      <div className="floating-fx-layer" aria-hidden="true">
        {floatingFx.map((fx) => (
          <span
            key={fx.id}
            className="floating-fx-item"
            style={
              {
                ['--fx-left' as any]: `${fx.left}%`,
                ['--fx-bottom' as any]: `${fx.bottom}%`,
                ['--fx-size' as any]: `${fx.size}px`,
                ['--fx-drift' as any]: `${fx.drift}px`,
                ['--fx-duration' as any]: `${fx.duration}ms`,
                ['--fx-delay' as any]: `${fx.delay}ms`,
              } as React.CSSProperties
            }
          >
            {fx.glyph}
          </span>
        ))}
      </div>
      {/* REPORT MODAL */}
      {reportOpen && (
        <div className="reportOverlay" onClick={() => !reportSubmitting && setReportOpen(false)} role="dialog" aria-modal="true">
          <div className="reportCard" onClick={(e) => e.stopPropagation()}>
            <div className="reportHeader">
              <div className="reportTitle">{i18n.t('mainLanding.report.title', { defaultValue: 'Report' })}</div>
              <button type="button" className="reportClose" onClick={() => !reportSubmitting && setReportOpen(false)}>
                <CloseIcon fontSize="small" />
              </button>
            </div>

            <div className="reportBody">
              <p className="reportHint">{i18n.t('mainLanding.report.hint', { defaultValue: 'Enter the reason you are reporting this.' })}</p>
              <textarea
                className="reportTextarea"
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                placeholder={i18n.t('mainLanding.report.placeholder', { defaultValue: 'Write reason...' })}
                rows={4}
                disabled={reportSubmitting}
              />
            </div>

            <div className="reportFooter">
              <button type="button" className="reportBtn ghost" onClick={() => setReportOpen(false)} disabled={reportSubmitting}>
                {i18n.t('mainLanding.actions.cancel', { defaultValue: 'Cancel' })}
              </button>
              <button type="button" className="reportBtn danger" onClick={submitReport} disabled={reportSubmitting}>
                {reportSubmitting
                  ? i18n.t('mainLanding.report.sending', { defaultValue: 'Sending...' })
                  : i18n.t('mainLanding.report.submit', { defaultValue: 'Report' })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Top Navigation Bar */}
      <nav className="top-nav" style={{position:"absolute", width:"100%"}}>
        <div className="nav-container">
          <div className="nav-left">
            <div className="logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <span className="logo-icon">📍</span>
              <span className="logo-text">ATech</span>
            </div>

            <label className="search-container" aria-label={i18n.t('mainLanding.search.label', { defaultValue: 'Search' })}>
              <SearchIcon className="search-icon" />
              <input
                type="text"
                placeholder={i18n.t('mainLanding.search.placeholder', { defaultValue: 'Search restaurants, cafes, events...' })}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setShowSearchResults(Boolean(searchQuery.trim()))}
                className="search-input"
              />
            </label>
          </div>

          <div className="nav-right">
            {user ? (
              <div className="notification-wrapper">
                <NotificationIcon />
              </div>
            ) : null}

            <button
              className={`mobile-nav-menu-btn ${showMobileNavMenu ? 'is-open' : ''}`}
              onClick={() => setShowMobileNavMenu((prev) => !prev)}
              type="button"
              aria-label={i18n.t('mainLanding.mobile.moreActions', { defaultValue: 'More actions' })}
              aria-expanded={showMobileNavMenu}
            >
              {showMobileNavMenu ? <CloseIcon fontSize="small" /> : <MoreVertIcon />}
            </button>

            <div className={`nav-actions ${showMobileNavMenu ? 'is-open' : ''}`}>
              <AppPreferenceControls variant="landing" />

              {!user ? (
                <button className="login-btn" onClick={() => navigate('/login')} type="button">
                  <PersonIcon sx={{ fontSize: 18, marginRight: '8px' }} />
                  <span>{i18n.t('mainLanding.auth.login', { defaultValue: 'Log in' })}</span>
                </button>
              ) : (
                <div className="user-dropdown" onClick={() => navigate(resolvePageRedirectRoute((user as any)?.pageRedirect))}>
                  {userAvatarUrl ? (
                    <img src={userAvatarUrl} alt={user?.userName || 'User'} className="user-avatar user-avatar-image" />
                  ) : (
                    <div className="user-avatar">{userInitial}</div>
                  )}
                  <span className="user-name">{user?.userName || i18n.t('mainLanding.user.fallback', { defaultValue: 'User' })}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {showSearchResults && (
        <div className="landing-search-overlay" onClick={() => setShowSearchResults(false)}>
          <div className="landing-search-modal" onClick={(e) => e.stopPropagation()}>
            <div className="landing-search-modal__header">
              <h3>{i18n.t('mainLanding.search.resultsTitle', { defaultValue: 'Search results' })}</h3>
              <button type="button" className="landing-search-close" onClick={closeLandingSearch}>
                <CloseIcon fontSize="small" />
              </button>
            </div>

            <div className="landing-search-modal__body">
              {!hasSearchResults ? (
                <div className="landing-search-empty">{i18n.t('mainLanding.search.empty', { defaultValue: 'No results found for "{{query}}".', query: debouncedSearch })}</div>
              ) : (
                <>
                  {searchResults.posts.length > 0 && (
                    <section className="landing-search-group">
                      <h4>{i18n.t('mainLanding.search.posts', { defaultValue: 'Posts' })}</h4>
                      {searchResults.posts.map((post) => (
                        <button
                          key={`post-${post.id}`}
                          type="button"
                          className="landing-search-result"
                          onClick={() => {
                            document.querySelector(`[data-post-id="${post.id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            setShowSearchResults(false);
                          }}
                        >
                          <strong>{post.userName || i18n.t('mainLanding.user.fallback', { defaultValue: 'User' })}</strong>
                          <span>{post.content}</span>
                        </button>
                      ))}
                    </section>
                  )}

                  {searchResults.companies.length > 0 && (
                    <section className="landing-search-group">
                      <h4>{i18n.t('mainLanding.search.businesses', { defaultValue: 'Businesses' })}</h4>
                      {searchResults.companies.map((company) => (
                        <button
                          key={`company-${company.id}`}
                          type="button"
                          className="landing-search-result"
                          onClick={() => {
                            const selected = locationOptions.find((item) => item.id === `company-${company.id}`);
                            if (selected) setSelectedMapLocationId(selected.id);
                            setShowSearchResults(false);
                          }}
                        >
                          <strong>{company.name}</strong>
                          <span>{company.location || company.website || i18n.t('mainLanding.search.businessFallback', { defaultValue: 'Business partner' })}</span>
                        </button>
                      ))}
                    </section>
                  )}

                  {searchResults.hotels.length > 0 && (
                    <section className="landing-search-group">
                      <h4>{i18n.t('mainLanding.search.hotels', { defaultValue: 'Hotels' })}</h4>
                      {searchResults.hotels.map((hotel) => (
                        <button
                          key={`hotel-${hotel.id}`}
                          type="button"
                          className="landing-search-result"
                          onClick={() => {
                            setShowSearchResults(false);
                            navigate(`/reservations/${toSlug(hotel.name)}`);
                          }}
                        >
                          <strong>{hotel.name}</strong>
                          <span>{hotel.address || i18n.t('mainLanding.search.defaultHotelAddress', { defaultValue: 'Durres, Albania' })}</span>
                        </button>
                      ))}
                    </section>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions Overlay */}
      {showQuickActions && (
        <div className="quick-actions-overlay" onClick={() => setShowQuickActions(false)}>
          <div className="quick-actions-menu" onClick={(e) => e.stopPropagation()}>
            <button className="quick-action" onClick={() => navigate('/create-post')} type="button">
              <AddPhotoAlternateIcon /> {i18n.t('mainLanding.quickActions.newPhoto', { defaultValue: 'New photo' })}
            </button>
            <button className="quick-action" onClick={() => navigate('/check-in')} type="button">
              <LocationOnIcon /> {i18n.t('mainLanding.quickActions.checkIn', { defaultValue: 'Check-in' })}
            </button>
            <button className="quick-action" onClick={() => navigate('/write-review')} type="button">
              <StarIcon /> {i18n.t('mainLanding.quickActions.review', { defaultValue: 'Review' })}
            </button>
            <button className="quick-action" onClick={() => navigate('/ask-question')} type="button">
              <QuestionAnswerIcon /> {i18n.t('mainLanding.quickActions.question', { defaultValue: 'Question' })}
            </button>
            <button className="quick-action close-quick" onClick={() => setShowQuickActions(false)} type="button">
              <CloseIcon /> {i18n.t('mainLanding.actions.close', { defaultValue: 'Close' })}
            </button>
          </div>
        </div>
      )}

      {selectedBusinessShowcase ? (
        <section className="landing-showcase-stage" aria-label={i18n.t('mainLanding.showcase.title', { defaultValue: 'Featured places' })}>
          <div className="landing-showcase-stage__background">
            <img
              key={selectedBusinessShowcase.id}
              src={selectedBusinessShowcase.imageUrl}
              alt={selectedBusinessShowcase.title}
              className="landing-showcase-stage__image"
              onError={(event) => {
                event.currentTarget.style.display = 'none';
              }}
            />
          </div>
          <div className="landing-showcase-stage__scrim" />

          <div className="landing-showcase-stage__content">
            <div className="landing-showcase-stage__copy">
              <span className="landing-showcase-stage__eyebrow">
                {selectedBusinessShowcase.type === 'hotel' ? <HotelIcon fontSize="small" /> : <StorefrontIcon fontSize="small" />}
                {selectedBusinessShowcase.meta}
              </span>
              <h3>{selectedBusinessShowcase.title}</h3>
              {selectedBusinessShowcase.subtitle ? <p className="landing-showcase-stage__subtitle">{selectedBusinessShowcase.subtitle}</p> : null}
              <p className="landing-showcase-stage__description">{selectedBusinessShowcase.description}</p>
              <div className="landing-showcase-stage__actions">
                <button
                  type="button"
                  className="landing-showcase-stage__primary"
                  onClick={() => openBusinessShowcaseUrl(selectedBusinessShowcase.redirectUrl)}
                  disabled={!selectedBusinessShowcase.redirectUrl}
                >
                  <LinkIcon fontSize="small" />
                  {selectedBusinessShowcase.actionLabel}
                </button>
              </div>
            </div>

            <div className="landing-business-slider landing-business-slider--stage">
              <div className="landing-business-slider__controls" aria-label={i18n.t('mainLanding.showcase.sliderControls', { defaultValue: 'Showcase slider controls' })}>
                <button
                  type="button"
                  className="landing-business-slider__control"
                  onClick={() => moveBusinessSlider('left')}
                  aria-label={i18n.t('mainLanding.showcase.previous', { defaultValue: 'Previous' })}
                >
                  &lt;
                </button>
                <button
                  type="button"
                  className="landing-business-slider__control"
                  onClick={() => moveBusinessSlider('right')}
                  aria-label={i18n.t('mainLanding.showcase.next', { defaultValue: 'Next' })}
                >
                  &gt;
                </button>
              </div>
              <div
                ref={businessSliderViewportRef}
                className="landing-business-slider__viewport"
                onPointerDown={handleBusinessSliderPointerDown}
                onPointerMove={handleBusinessSliderPointerMove}
                onPointerUp={handleBusinessSliderPointerUp}
                onPointerCancel={(e) => stopBusinessSliderDrag(e.pointerId)}
                onPointerLeave={(e) => stopBusinessSliderDrag(e.pointerId)}
              >
                <div className="landing-business-slider__track">
                  {[...businessShowcase, ...businessShowcase].map((item, index) => (
                    <button
                      key={`${item.id}-${index}-${item.id}`}
                      type="button"
                      data-business-card-id={item.id}
                      className={`landing-business-card landing-business-card--${item.type} ${selectedBusinessShowcase.id === item.id ? 'is-active' : ''}`}
                      onClick={() => {
                        if (businessSliderDragRef.current.hasDragged) {
                          businessSliderDragRef.current.hasDragged = false;
                          return;
                        }
                        setSelectedBusinessId(item.id);
                      }}
                    >
                      <div className="landing-business-card__media">
                        <img
                          src={item.imageUrl}
                          alt={item.title}
                          className="landing-business-card__image"
                          loading="lazy"
                          onError={(event) => {
                            event.currentTarget.style.display = 'none';
                          }}
                        />

                        <div className="landing-business-card__content">
                          <span className="landing-business-card__type">{item.meta}</span>
                          <div className="landing-business-card__chip">
                            <strong>{item.title}</strong>
                            {item.subtitle ? (
                              <span className="landing-business-card__subtitle" title={item.subtitle}>
                                {item.subtitle}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : null}
      <div className="main-content">
        <aside className="left-sidebar">
          {renderMapSidebarSection('sidebar')}
        </aside>

        {/* Main Feed */}
        <main className="main-feed">
          <div className="feed-content">
            {/* Add Post */}

            {/* Posts */}
            <FeedPage
              posts={posts}
              user={user}
              onAddPost={handleAddPost}
              suggestedTags={suggestedTags}
              locationOptions={locationOptions}
              onLocationSelect={(location) => {
                if (location.mapTargetId) {
                  setSelectedMapLocationId(location.mapTargetId);
                }
              }}
              onAddComment={handleAddComment}
              onToggleLike={toggleLike}
              isLiked={(postId) => likedPostIds.has(postId)}
              shareOpenFor={shareOpenFor}
              onToggleShareMenu={(postId) => setShareOpenFor((prev) => (prev === postId ? null : postId))}
              onShareNative={async (postId) => {
                const url = getPostShareUrl(postId);
                try {
                  if (navigator.share) {
                    await navigator.share({ title: 'TechPos', text: i18n.t('mainLanding.feed.sharePostText', { defaultValue: 'Check this post:' }), url });
                    setShareOpenFor(null);
                    return;
                  }
                  await copyToClipboard(url);
                  setShareOpenFor(null);
                } catch {}
              }}
              onShareWhatsapp={(postId) => {
                const url = getPostShareUrl(postId);
                const text = encodeURIComponent(`${i18n.t('mainLanding.feed.sharePostText', { defaultValue: 'Check this post:' })} ${url}`);
                window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
                setShareOpenFor(null);
              }}
              onCopyShareLink={async (postId) => {
                const url = getPostShareUrl(postId);
                await copyToClipboard(url);
                setShareOpenFor(null);
              }}
              onOpenReport={openReport}
              onDeletePost={onDeletePost}
              onCelebrate={(kind) => {
                if (kind === 'like') spawnFloatingFx('sm');
              }}
            />
          </div>
        </main>

        <aside className="right-sidebar">
          <div className="sidebar-section">
            <h3 className="sidebar-title">{i18n.t('mainLanding.sidebar.categories', { defaultValue: 'Categories' })}</h3>
            <div className="categories-list">
              {categories.map((cat, idx) => (
                <button key={cat.id} className="category-item" type="button">
                  <span className="category-icon" style={{ color: '#4f46e5' }}>
                    {['R', 'C', 'B', 'S', 'E', 'O'][idx % 6]}
                  </span>
                  <span className="category-name">{cat.name} ({cat.postCount})</span>
                </button>
              ))}
            </div>
          </div>

          <div className="sidebar-section">
            <h3 className="sidebar-title">{i18n.t('mainLanding.sidebar.trendingNow', { defaultValue: 'Trending now' })}</h3>
            <div className="trending-list">
              {trendingHashtags.map((tag, idx) => (
                <div key={idx} className="trending-item">
                  <span className="trending-rank">#{idx + 1}</span>
                  <span className="trending-tag">{tag.tag}</span>
                  <span className="trending-count">{i18n.t('mainLanding.sidebar.postsCount', { defaultValue: '{{count}} posts', count: tag.postCount })}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="sidebar-section">
            <button
              className="sidebar-link-button"
              type="button"
              onClick={() => navigate('/booking')}
              aria-label={i18n.t('mainLanding.sidebar.hotels', { defaultValue: 'Hotels' })}
            >
              <span className="sidebar-link-button__content">
                <HotelIcon fontSize="small" />
                <span>{i18n.t('mainLanding.sidebar.hotels', { defaultValue: 'Hotels' })}</span>
              </span>
            </button>
            <div className="events-list hotel-list">
              {sidebarHotels.length === 0 ? (
                <div className="hotel-card hotel-card--empty">
                  <div className="event-info">
                    <h4 className="event-title">{i18n.t('mainLanding.sidebar.noHotels', { defaultValue: 'No hotels found' })}</h4>
                    <p className="event-location">{i18n.t('mainLanding.sidebar.noHotelsHint', { defaultValue: 'The list will be filled from /booking as soon as the API returns data.' })}</p>
                  </div>
                </div>
              ) : (
                sidebarHotels.map((hotel, index) => (
                  <button
                    key={hotel.id}
                    className="event-card hotel-card"
                    type="button"
                    onClick={() => navigate(`/reservations/${toSlug(hotel.name)}`)}
                  >
                    <div className="event-date hotel-order">
                      <span className="event-day">{index + 1}</span>
                      <span className="event-month">{i18n.t('mainLanding.businessCard.hotel', { defaultValue: 'Hotel' })}</span>
                    </div>
                    <div className="event-info">
                      <h4 className="event-title">{hotel.name}</h4>
                      <p className="event-location">{hotel.address || i18n.t('mainLanding.search.defaultHotelAddress', { defaultValue: 'Durres, Albania' })}</p>
                      {hotel.note ? <p className="hotel-note">{hotel.note}</p> : null}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          <div className="sidebar-section">
            <h3 className="sidebar-title">{i18n.t('mainLanding.sidebar.dailyStats', { defaultValue: 'Today stats' })}</h3>
            <div className="daily-stats">
              <div className="stat-card">
                <div className="stat-icon">+</div>
                <div className="stat-content">
                  <span className="stat-value">{dailyStats.postsToday}</span>
                  <span className="stat-label">{i18n.t('mainLanding.sidebar.newPosts', { defaultValue: 'New posts' })}</span>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon">*</div>
                <div className="stat-content">
                  <span className="stat-value">{dailyStats.visitorsToday}</span>
                  <span className="stat-label">{i18n.t('mainLanding.sidebar.activeVisitors', { defaultValue: 'Active visitors' })}</span>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>
      {isExpandedMapOpen ? (
        <div className="landing-map-modal-backdrop" role="presentation" onClick={() => setIsExpandedMapOpen(false)}>
          <div
            className="landing-map-modal"
            role="dialog"
            aria-modal="true"
            aria-label={i18n.t('mainLanding.map.expandedTitle', { defaultValue: 'Expanded map view' })}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="landing-map-modal-close"
              onClick={() => setIsExpandedMapOpen(false)}
              aria-label={i18n.t('mainLanding.actions.close', { defaultValue: 'Close' })}
              title={i18n.t('mainLanding.actions.close', { defaultValue: 'Close' })}
            >
              <CloseIcon fontSize="small" />
            </button>
            {renderMapSidebarSection('expanded')}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const MainLanding: React.FC = () => {
  return <MainLandingContent />;
};

export default MainLanding;



